import Vue from 'vue'
import { convertUpdateTypeToChangeType } from '../utils/change-type'
import { jobScope, rememberedJob, resumeRememberedJob, submitAndAwaitJob } from './async-job'
import { getExcelUploadStash } from './excel-upload-stash'
import {
  DEFAULT_PANEL_SETTING,
  getAxiosErrorMessage,
  getBaseUrl,
  recordsEqual,
} from './helpers'
import { parseExcelUploadFile } from './parse-excel-upload'

// Per project: a session that switches projects must still be able to resume the other one.
const excelJobResumeStarted = new Set()

function applyExcelBatchOutcome(dispatch, outcome) {
  const payload = outcome.result || {}
  const errors = payload.errors || []
  const code = outcome.async ? 0 : outcome.envelope && outcome.envelope.code
  if (!code && errors.length == 0) {
    dispatch('handleMessage', {
      success: true,
      text: outcome.async ? '非同步上傳已完成' : '上傳成功',
    })
  } else if (!code && errors.length > 0) {
    const errorKeys = errors.map(
      (ele) => `${Object.keys(ele)[0]}: ${Object.values(ele)[0]}`
    )
    dispatch('handleMessage', {
      success: false,
      text: `上傳失敗的key:\n ${errorKeys.join('\n')}`,
    })
  } else {
    dispatch('handleMessage', {
      success: false,
      text: (outcome.envelope && outcome.envelope.msg) || '上傳失敗',
    })
  }
}

export default {

    async getDiff({ getters, commit, dispatch }, ticket) {
      commit('setIsLoading', true)
      try {
        const res = await Vue.axios.get(`${getBaseUrl()}/snapshot/review`, {
          params: {
            project: getters.project,
            ticket,
          },
        })
        if (res.data.code === 0) {
          commit('setCardsHistory', res.data.data.changes)
        } else {
          dispatch('handleMessage', {
            success: false,
            text: res.data.msg,
          })
        }
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error),
        })
      } finally {
        commit('setIsLoading', false)
      }
    },
    async getProjects({ getters, commit, dispatch }, { currentProject }) {
      if (getters.isLoading || getters.projects.length > 0) {
        return
      }
      commit('setIsLoading', true)
      let selectedProject = null
      try {
        const res = await Vue.axios.get(`${getBaseUrl()}/translation`)
        if (res.data.code === 0) {
          commit('setProjects', res.data.data.projectList)

          selectedProject = res.data.data.projectList.includes(currentProject)
            ? currentProject
            : res.data.data.projectList[0]
        } else {
          commit('setProjects', [])
          dispatch('handleMessage', {
            success: false,
            text: res.data.msg,
          })
        }
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error),
        })
      } finally {
        commit('setIsLoading', false)
      }
      // Set project after clearing loading so the project watcher can run getData.
      if (selectedProject) {
        commit('setProject', selectedProject)
      }
    },
    async approveTicket({ getters, commit, dispatch }, { tickets, payload }) {
      try {
        await submitAndAwaitJob({
          method: 'post',
          url: `${getBaseUrl()}/ticket/${getters.project}/approve`,
          data: {
            tickets,
            ticketVersion: getters.ticketVersion,
          },
          operation: 'ticketApprove',
        })
        commit('setTicketVersion', null)
        dispatch('getUpdatedTickets', payload)
        commit('setApprovedTickets', [])
        commit('setIsConfirmApproveModalOpen', false)
        dispatch('handleMessage', {
          success: true,
          text: '成功審核',
        })
        return []
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: error?.message || '操作頻繁，請稍候再試',
        })
        dispatch('getUpdatedTickets', payload)
        dispatch('getUpdatedTicketsPage')
        commit('setApprovedTickets', [])
        commit('setIsConfirmApproveModalOpen', false)
      }
    },
    async getData({ getters, commit, dispatch }) {
      if (getters.isLoading === true) return
      commit('setIsLoading', true)
      try {
        const res = await Vue.axios.get(
          `${getBaseUrl()}/translation/${getters.project}`,
          {
            params: {
              env: getters.env,
            },
          }
        )
        if (res.data.code === 0) {
          commit('setData', res.data.data.records)
          if (getters.env === 'DEV' && res.data.data.devVersion != null) {
            commit('setDevVersion', res.data.data.devVersion)
          }
          if (getters.env === 'DEV' && res.data.data.qaVersion != null) {
            commit('setQaVersion', res.data.data.qaVersion)
          }
        } else {
          commit('setData', [])
          dispatch('handleMessage', {
            success: false,
            text: res.data.msg,
          })
        }
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error),
        })
      } finally {
        commit('setIsLoading', false)
      }
    },

    /**
     * Silent GET after local skip-GET success: only replace page data when server differs.
     * Does not toggle isLoading so the UI stays responsive.
     */
    async reconcileData({ getters, commit }) {
      try {
        const res = await Vue.axios.get(
          `${getBaseUrl()}/translation/${getters.project}`,
          {
            params: {
              env: getters.env,
            },
          }
        )
        if (res?.data?.code !== 0 || !res.data.data) return
        const serverRecords = res.data.data.records || []
        if (!recordsEqual(getters.data, serverRecords)) {
          commit('setData', serverRecords)
        }
        if (getters.env === 'DEV' && res.data.data.devVersion != null) {
          if (getters.devVersion !== res.data.data.devVersion) {
            commit('setDevVersion', res.data.data.devVersion)
          }
        }
        if (getters.env === 'DEV' && res.data.data.qaVersion != null) {
          commit('setQaVersion', res.data.data.qaVersion)
        }
      } catch (_) {
        // Best-effort verify; keep local page data on network errors.
      }
    },

    async updateData({ getters, commit, dispatch }, body) {
      // 檢測報錯
      let status = false
      let shouldRefresh = true
      let shouldReconcile = false
      if (getters.isLoading === true) return
      commit('setIsLoading', true)

      if (body['zh_CN'] === '') {
        dispatch('handleMessage', {
          success: false,
          text: '更新失敗，簡體中文不能為空',
        })
        commit('setIsLoading', false)
        dispatch('getData')
        return
      }

      try {
        const res = await Vue.axios.put(
          `${getBaseUrl()}/translation/${getters.project}`,
          body
        )
        if (res?.data?.code === 0) {
          dispatch('handleMessage', {
            success: true,
            text: '更新成功',
          })
          status = true
          const keyExists = getters.data.some((record) => record.key === body.key)
          if (
            getters.panelSetting.skipGetAfterUpdateData &&
            body.key &&
            keyExists
          ) {
            commit('patchRecordInData', body)
            shouldRefresh = false
            shouldReconcile = getters.panelSetting.reconcileAfterUpdateData
          }
        } else {
          dispatch('handleMessage', {
            success: false,
            text: res?.data?.msg,
          })
        }
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error),
        })
      } finally {
        commit('setIsLoading', false)
      }
      if (shouldRefresh) {
        dispatch('getData')
      } else if (shouldReconcile) {
        dispatch('reconcileData')
      }
      return status
    },
    async updateTicket({ getters, commit, dispatch }, body) {
      // 檢測報錯
      let status = false
      let shouldRefresh = true
      let shouldReconcile = false
      if (getters.isLoading === true) return
      commit('setIsLoading', true)

      let recordId = body.id
      // Ticket PUT requires Mongo ObjectId. Client-only ids (from older skip-GET adds) cannot be used.
      const isMongoObjectId = /^[a-fA-F0-9]{24}$/.test(String(recordId || ''))
      if (!isMongoObjectId) {
        commit('setIsLoading', false)
        dispatch('getData')
        dispatch('handleMessage', {
          success: false,
          text: '資料已重新載入，請再試一次修改單號',
        })
        return false
      }

      try {
        const res = await Vue.axios.put(
          `${getBaseUrl()}/translation/${getters.project}/${recordId}`,
          { newTicket: body.newTicket }
        )
        if (res?.data?.code === 0) {
          dispatch('handleMessage', {
            success: true,
            text: '更新成功',
          })
          status = true
          const idExists = getters.data.some((record) => record._id === recordId)
          if (
            getters.panelSetting.skipGetAfterUpdateTicket &&
            recordId &&
            idExists
          ) {
            commit('patchTicketById', {
              id: recordId,
              ticket: body.newTicket,
            })
            shouldRefresh = false
            shouldReconcile = getters.panelSetting.reconcileAfterUpdateTicket
          }
        } else {
          dispatch('handleMessage', {
            success: false,
            text: res?.data?.msg,
          })
        }
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error),
        })
      } finally {
        commit('setIsLoading', false)
      }
      if (shouldRefresh) {
        dispatch('getData')
      } else if (shouldReconcile) {
        dispatch('reconcileData')
      }
      return status
    },
    async addData({ getters, commit, dispatch }, { body }) {
      let status = false
      let shouldRefresh = true
      let shouldReconcile = false
      if (getters.isLoading === true) return
      commit('setIsLoading', true)
      try {
        const res = await Vue.axios.post(
          `${getBaseUrl()}/translation/${getters.project}`,
          {
            env: getters.env,
            ...body,
          }
        )
        if (res?.data?.code === 0) {
          dispatch('handleMessage', {
            success: true,
            text: '新增成功',
          })
          status = true
          const keyExists = getters.data.some((record) => record.key === body.key)
          if (
            getters.panelSetting.skipGetAfterAddData &&
            body.key &&
            !keyExists
          ) {
            const createdId = res.data.data && res.data.data._id
            commit('addRecordInData', {
              ...body,
              _id: createdId || `local-${Date.now()}`,
              version:
                (res.data.data && res.data.data.version) != null
                  ? res.data.data.version
                  : getters.devVersion,
            })
            shouldRefresh = false
            shouldReconcile = getters.panelSetting.reconcileAfterAddData
          }
        } else {
          dispatch('handleMessage', {
            success: false,
            text: res?.data?.msg,
          })
        }
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error),
        })
      } finally {
        commit('setIsLoading', false)
      }
      if (shouldRefresh) {
        dispatch('getData')
      } else if (shouldReconcile) {
        dispatch('reconcileData')
      }
      return status
    },

    async handleMessage({ commit }, { success, text, durationMs }) {
      commit('addMessage', {
        success,
        text,
        id: new Date().getTime() + Math.floor(Math.random() * 10),
      })
      const waitMs =
        Number.isFinite(durationMs) && durationMs > 0 ? durationMs : 4000
      await new Promise((resolve) => setTimeout(resolve, waitMs))
      commit('clearMessage')
    },

    async getUser({ commit, dispatch }) {
      const res = await Vue.axios.get(`${getBaseUrl()}/auth/user`)
      if (res.data.data) {
        commit('setUser', res.data.data.user)
      }
      await dispatch('fetchPanelSetting')
    },

    async fetchPanelSetting({ commit }) {
      try {
        const res = await Vue.axios.get(`${getBaseUrl()}/panel/setting`)
        if (res?.data?.code === 0 && res.data.data) {
          commit('setPanelSetting', res.data.data)
        } else {
          commit('setPanelSetting', { ...DEFAULT_PANEL_SETTING })
        }
      } catch (_) {
        commit('setPanelSetting', { ...DEFAULT_PANEL_SETTING })
      }
    },

    async updatePanelSetting({ commit, dispatch }, setting) {
      try {
        const res = await Vue.axios.put(`${getBaseUrl()}/panel/setting`, setting)
        if (res?.data?.code === 0 && res.data.data) {
          commit('setPanelSetting', res.data.data)
          dispatch('handleMessage', {
            success: true,
            text: '面板設定已儲存',
          })
          return true
        }
        dispatch('handleMessage', {
          success: false,
          text: res?.data?.msg || '面板設定儲存失敗',
        })
        return false
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error),
        })
        return false
      }
    },

    async updateMany({ getters, commit, dispatch }, { data, selectedLang, file }) {
      if (getters.isLoading === true) return
      commit('setIsLoading', true)
      const url = `${getBaseUrl()}/excel/batch/${getters.project}`
      try {
        const outcome = await submitAndAwaitJob({
          method: 'post',
          url,
          data: {
            data,
            selectedLang,
          },
          operation: 'excelBatch',
          project: getters.project,
          file: getters.panelSetting.asyncExcelBatch ? file : undefined,
        })
        applyExcelBatchOutcome(dispatch, outcome)
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error, '上傳失敗'),
        })
      } finally {
        commit('setIsLoading', false)
      }
      dispatch('getData')
    },

    async resumePendingExcelJob({ getters, commit, dispatch }) {
      if (excelJobResumeStarted.has(getters.project)) {
        return
      }
      const url = `${getBaseUrl()}/excel/batch/${getters.project}`
      const pending =
        rememberedJob(jobScope('excelBatch', 'post', url)) || rememberedJob('excelBatch')
      const stash = await getExcelUploadStash(getters.project)
      if (
        (!pending || (!pending.jobId && !pending.idempotencyKey)) &&
        !(stash && stash.file)
      ) {
        excelJobResumeStarted.add(getters.project)
        return
      }
      excelJobResumeStarted.add(getters.project)
      for (let i = 0; i < 80 && getters.isLoading; i++) {
        await new Promise((resolve) => setTimeout(resolve, 50))
      }
      commit('setIsLoading', true)
      try {
        let outcome = await resumeRememberedJob({
          operation: 'excelBatch',
          method: 'post',
          url,
          operationId: 'excelBatch',
          project: getters.project,
        })
        if (outcome && outcome.needsReplay) {
          const rows = await parseExcelUploadFile(outcome.file)
          outcome = await submitAndAwaitJob({
            method: 'post',
            url,
            data: {
              data: rows,
              selectedLang: outcome.selectedLang,
            },
            operation: 'excelBatch',
            project: getters.project,
            file: outcome.file,
            replay: true,
          })
        }
        if (!outcome) {
          dispatch('handleMessage', {
            success: false,
            text: '找不到進行中的上傳，請重新選擇檔案',
          })
          return
        }
        applyExcelBatchOutcome(dispatch, outcome)
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error, '上傳失敗'),
        })
      } finally {
        commit('setIsLoading', false)
        dispatch('getData')
      }
    },

    async deleteData({ getters, commit, dispatch }, { key }) {
      let shouldRefresh = true
      let shouldReconcile = false
      if (getters.isLoading === true) return
      commit('setIsLoading', true)
      try {
        const request_config = { data: { key: `${key}` } }
        const res = await Vue.axios.post(
          `${getBaseUrl()}/admin/delete/${getters.project}`,
          request_config,
          { withCredentials: true }
        )
        if (res?.data?.status === true) {
          dispatch('handleMessage', {
            success: true,
            text: '刪除成功',
          })
          const keyExists = getters.data.some((record) => record.key === key)
          if (getters.panelSetting.skipGetAfterDeleteData && key && keyExists) {
            commit('removeRecordByKey', key)
            shouldRefresh = false
            shouldReconcile = getters.panelSetting.reconcileAfterDeleteData
          }
        } else {
          dispatch('handleMessage', {
            success: false,
            text: res?.data?.msg,
          })
        }
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error),
        })
      } finally {
        commit('setIsLoading', false)
      }
      if (shouldRefresh) {
        dispatch('getData')
      } else if (shouldReconcile) {
        dispatch('reconcileData')
      }
    },

    async getHistory({ getters, commit, dispatch }, payload) {
      // if (getters.isLoading === true) return
      // commit('setIsLoading', true)
      try {
        const daysForRecords = 30 // TODO: add date picker in UI
        const { key, updateUser, updateType, pageNum, pageSize } = payload
        const res = await Vue.axios.get(`${getBaseUrl()}/audit-log`, {
          params: {
            project: getters.project,
            env: getters.env,
            key,
            operator: updateUser,
            changeType: convertUpdateTypeToChangeType(updateType),
            pageNum,
            pageSize,
            startDate: new Date(
              new Date().setDate(new Date().getDate() - daysForRecords)
            ).setHours(0, 0, 0, 0),
            endDate: new Date().setHours(23, 59, 59, 999),
          },
        })
        commit('setHistoryData', res.data.data.records)
        commit('setHistoryPageData', res.data.data.info)
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: '查詢錯誤',
        })
      }
      // finally {
      //
      //     commit('setIsLoading', false)
      // }
    },
    async getUpdatedTickets({ getters, commit, dispatch }, payload = {}) {
      commit('setIsLoading', true)
      try {
        const { searchTicket, pageNum, pageSize } = payload
        const res = await Vue.axios.get(`${getBaseUrl()}/ticket`, {
          params: {
            project: getters.project,
            searchTicket: searchTicket,
            pageNum,
            pageSize,
          },
        })
        if (res.data.code === 0) {
          if (res.data.data.tickets.length) {
            commit('setUpdatedTickets', res.data.data.tickets[0]['tickets'])
            commit('setUpdatedTicketsPage', [
              res.data.data.tickets[0]['totalCount'],
              res.data.data.tickets[0]['totalPages'],
            ])
          } else {
            commit('setUpdatedTickets', res.data.data.tickets)
            commit('setUpdatedTicketsPage', [0, 1])
          }
          commit('setTicketVersion', res.data.data.ticketVersion)
        } else {
          dispatch('handleMessage', {
            success: false,
            text: res.data.msg,
          })
        }
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: '系統錯誤',
        })
      } finally {
        commit('setIsLoading', false)
      }
    },
    async getDeployableVersions({ getters, commit, dispatch }, payload = {}) {
      commit('setIsLoading', true)
      try {
        const { pageNum, pageSize, searchTicket } = payload
        let uri = `${getBaseUrl()}/project/${getters.project}`
        // if (ticket) {
        //     uri += `/?ticket=${ticket}`
        // }
        const res = await Vue.axios.get(uri, {
          params: {
            searchTicket,
            pageNum,
            pageSize,
          },
        })
        if (res.data.code === 0) {
          if (res.data.data.metaData) {
            commit('setCurrentVersion', res.data.data.metaData.PROD)
            commit('setDeployableVersions', res.data.data.ticketList)
            commit('setDeployableVersionsPage', [
              res.data.data.metaData['totalCount'],
              res.data.data.metaData['totalPage'],
            ])
          } else {
            dispatch('handleMessage', {
              success: false,
              text: '查無此工單號',
            })
          }
        } else {
          commit('setDeployableVersionsPage', [0, 1])
          dispatch('handleMessage', {
            success: false,
            text: res.data.msg,
          })
        }
      } catch (error) {
        dispatch('handleMessage', {
          success: false,
          text: '系統錯誤',
        })
      } finally {
        commit('setIsLoading', false)
      }
    },
    async updateProductionVersion(
      { getters, commit, dispatch },
      { version, payload }
    ) {
      commit('setIsLoading', true)
      try {
        const outcome = await submitAndAwaitJob({
          method: 'put',
          url: `${getBaseUrl()}/project/${getters.project}/productionVersion`,
          data: {
            version,
          },
          operation: 'productionVersion',
        })
        const code = outcome.async ? 0 : outcome.envelope && outcome.envelope.code
        if (code === 0) {
          dispatch('getDeployableVersions', payload)
          commit('setIsConfirmDeployModalOpen', false)
          dispatch('handleMessage', {
            success: true,
            text: '成功上版',
          })
        } else {
          dispatch('handleMessage', {
            success: false,
            text: (outcome.envelope && outcome.envelope.msg) || '系統錯誤',
          })
        }
      } catch (error) {
        commit('setIsConfirmDeployModalOpen', false)
        dispatch('handleMessage', {
          success: false,
          text: getAxiosErrorMessage(error, '系統錯誤'),
        })
      } finally {
        commit('setIsLoading', false)
      }
    },
    async getProjectSettings({ getters, commit, dispatch }) {
      const res = await Vue.axios.get(`${getBaseUrl()}/project/${getters.project}/settings`)
      if (res.data.code === 0 && res.data.data && res.data.data.settings) {
        commit('setNeedValidateKey', res.data.data.settings.needValidateKey)
      } else {
        commit('setNeedValidateKey', false)
        dispatch('handleMessage', {
          success: false,
          text: res.data.msg,
        })
      }
    },
  }
